import { Button } from '@components/button';
import { DataTable } from '@components/dataTable';
import { BenchUser } from '@root/globalTypes';
import { ColumnDef } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCheckUserPermissions } from '@hooks/checkUserPermisions';
import { useTableSorting } from '@hooks/useTableSorting';
import { TableDateCell } from '@components/table/dateCell';
import { DeleteBenchUserDialog } from '@components/dialog/deleteBenchUser';

type BenchUsersTableProps = {
  data: BenchUser[];
  visibleColumns?: string[];
  onRowSelect: (id: string) => void;
};

export function useBenchUsersTable({ data, visibleColumns, onRowSelect }: BenchUsersTableProps) {
  const { t } = useTranslation('app');
  const { verifyUserPermission } = useCheckUserPermissions();
  const { sorting, updateSorting } = useTableSorting();

  const columns: ColumnDef<BenchUser>[] = [
    {
      id: 'username',
      accessorKey: 'username',
      header: t('table.username'),
      sortDescFirst: true,
      enableSorting: true,
    },
    {
      id: 'expires_at',
      accessorKey: 'expiresAt',
      header: t('table.expiresAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.expiresAt} />,
    },
    {
      id: 'created_at',
      accessorKey: 'createdAt',
      header: t('table.createdAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.createdAt} />,
    },
    {
      id: 'updated_at',
      accessorKey: 'updatedAt',
      header: t('table.updatedAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.updatedAt} />,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: t('table.actions'),
      enableSorting: false,
      cell: ({ row }) => {
        const benchUser = row.original;
        return (
          <div className="flex items-center gap-2">
            {verifyUserPermission('bench_users_update') && (
              <Button
                variant="primary"
                size="sm"
                className="w-14"
                onClick={() => onRowSelect(benchUser.id)}>
                <PencilIcon className="h-5" />
              </Button>
            )}
            <DeleteBenchUserDialog benchUser={benchUser} />
          </div>
        );
      },
    },
  ];

  return {
    columns,
    Table: () => {
      return (
        <DataTable
          columns={
            visibleColumns
              ? columns.filter(({ id }) =>
                  typeof id === 'string' ? visibleColumns.includes(id) : false,
                )
              : columns
          }
          data={data}
          sorting={sorting}
          onSortChange={updateSorting}
        />
      );
    },
  };
}
